<template>
  <div class="w-full max-w-sm mx-auto">
    <!-- Header -->
    <div class="text-3xl font-semibold text-[#00CAAA] text-center mb-2">
       <!-- back button -->
         <button
          type="button"
          @click="emit('close')"
          class="text-sm text-[#00ccaa] hover:text-[#00eedd] hidden lg:flex items-center cursor-pointer lg:mt-2"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g
      clip-path="url(#clip0_1477_25604)"
      data-v-inspector="src/components/icons/IconRanaCross.vue:3:5"
    >
      <path
        d="M6.01041 18.0104L18.0312 5.98959M18.0312 18.0104L6.01041 5.98959"
        stroke="#00ccaa"
        stroke-width="3"
        stroke-linecap="round"
        stroke-linejoin="round"
        data-v-inspector="src/components/icons/IconRanaCross.vue:4:7"
      ></path>
    </g>
    <defs data-v-inspector="src/components/icons/IconRanaCross.vue:12:5">
      <clipPath
        id="clip0_1477_25604"
        data-v-inspector="src/components/icons/IconRanaCross.vue:13:7"
      >
        <rect
          width="24"
          height="24"
          fill="white"
          data-v-inspector="src/components/icons/IconRanaCross.vue:14:9"
        ></rect>
      </clipPath>
    </defs>
  </svg>
        </button>

      <div v-if="backFlag" class="flex items-center justify-center mb-2">
        <span class="ml-4">Sign In</span>
      </div>
      <div v-else>
        Sign In 
      </div>
    </div>
    <div class="h-[2px] w-64 mx-auto bg-gradient-to-r from-transparent via-[#00CAAA] to-transparent opacity-60 mb-4"></div>
    <p class="text-center mb-4">Access your account securely.</p>

    <!-- Terms of Service Modal -->
    <div
      v-if="tos"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <ranaTos
        v-model="tos"
        @submited="handleTos"
        class="bg-white rounded-xl shadow-xl max-h-[90vh] overflow-auto"
      />
    </div>

    <!-- Messages -->
    <div v-if="msgFlag" class="justify-center w-full text-center mb-4">
      <p class="text-shadow-white" v-html="msg"></p>
    </div>
      <Alert 
      v-if="alertFlag" 
      variant="destructive" 
      class="mb-4 shadow-sm relative overflow-hidden border border-primary rounded-lg p-2"
      :class="{ 'slide-in-right': alertFlag }"
    >
      <div class="absolute top-0 left-0 w-1 h-full bg-destructive"></div>
      <div class="flex items-start gap-3">
       
        <div class="flex-1">
          <AlertTitle class="font-medium text-destructive">Authentication Error</AlertTitle>
          <AlertDescription class="text-sm mt-1">
            <p v-html="msg"></p>
          </AlertDescription>
        </div>
      </div>
    </Alert>

    <!-- Form Inputs -->
    <div class="p-4 pb-0 space-y-4 mb-4">
      <div>
        <input
          :disabled="lock"
          id="email"
          type="email"
          v-model="email"
          @keydown.enter="checkEmail"
          class="w-full px-3 py-2 mt-1 border rounded bg-background text-foreground border-primary focus:outline-none focus:ring-2 focus:ring-[#00CAAA] focus:border-transparent"
          placeholder="you@example.com"
        />
      </div>

      <div v-if="qrImg && !tos">
        <img :src="qrImg" alt="img" class="mx-auto py-3.5" />
      </div>

      <div v-if="pinFlag">
        <PinInput
          id="pin-input"
          v-model="pin"
          placeholder="○"
          @complete="pinCall"
          class="flex justify-between w-full gap-2"
        >
          <PinInputGroup class="flex justify-between w-full gap-2">
            <PinInputSlot
              v-for="(id, index) in 6"
              :key="id"
              :index="index"
              class="w-full py-2 text-center border rounded bg-background text-foreground border-primary focus:outline-none focus:ring-2 focus:ring-[#00CAAA] focus:border-transparent"
            />
          </PinInputGroup>
        </PinInput>
      </div>

      <!-- Additional fields only for registration -->
      <div v-if="infoFlag" class="space-y-4">
        <label class="block text-sm font-medium">
          Date of Birth
          <input
            type="date"
            v-model="dateOfBirth"
            class="w-full px-3 py-2 mt-1 border rounded bg-background text-foreground border-primary focus:outline-none focus:ring-2 focus:ring-[#00CAAA] focus:border-transparent"
          />
        </label>

        <label class="block text-sm font-medium">
          Region
          <select
            v-model="region"
            class="w-full px-3 py-2 mt-1 border rounded bg-background text-foreground border-primary focus:outline-none focus:ring-2 focus:ring-[#00CAAA] focus:border-transparent"
          >
            <option
              v-for="(name, code) in countryList"
              :key="code"
              :value="code"
            >
              {{ name }}
            </option>
          </select>
        </label>

        <Button class="w-full" @click="submitInfo">Submit</Button>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex justify-center">
      <Button v-if="step1" class="w-1/3 bg-transparent border border-primary" @click="checkEmail">Continue</Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Button } from './ui/button'
import { Alert, AlertDescription, AlertTitle } from './ui/alert'
import { PinInput, PinInputGroup, PinInputSlot } from './ui/pin-input'
import ranaTos from './ranaTos'
import countries from 'i18n-iso-countries'
import enLocale from 'i18n-iso-countries/langs/en.json'
import { useRuntimeConfig, useCookie } from '#app'
import { useAuthToken, useAuthEmail } from '../composables/useAuthDrawer'

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'closeLogin'): void
}>()

countries.registerLocale(enLocale)
const countryList = countries.getNames('en')

const email = ref('')
const pin = ref<string[]>([])
const alertFlag = ref(false)
const pinFlag = ref(false)
const regFlag = ref(false)
const msgFlag = ref(false)
const lock = ref(false)
const step1 = ref(true)
const infoFlag = ref(false)
const backFlag = ref(false)

const msg = ref<string | null>(null)
const tos = ref(false)
const fetchEmailResp = ref<any>(null)
const token = ref<string | null>(null)
const qrImg = ref<string | null>(null)
const valKey = ref(0)
const dateOfBirth = ref('')
const region = ref('')

const config = useRuntimeConfig()

const api = import.meta.env.VITE_CLOUD_URL

function resetForm() {
  email.value = ''
  pin.value = []
  pinFlag.value = false
  regFlag.value = false
  msgFlag.value = false
  lock.value = false
  step1.value = true
  msg.value = null
  token.value = null
  qrImg.value = null
  valKey.value = 0
  fetchEmailResp.value = null
  dateOfBirth.value = ''
  region.value = ''
  infoFlag.value = false
  backFlag.value = false
  tos.value = false
}

function clearMSG() {
  setTimeout(() => {
    alertFlag.value = false
  }, 5000)
}

function pinCall() {
  if (valKey.value === 0) login()
  else conf2fa()
}

async function checkEmail() {

  backFlag.value = true
  if (!email.value) {
    alertFlag.value = true
    msg.value = 'Please enter your email'
    clearMSG()
    return
  }
  if (!email.value.includes('@') || !email.value.includes('.')) {
    alertFlag.value = true
    msg.value = 'Please enter a valid email'
    clearMSG()
    return
  }
  const req = await fetch(`${api}totp/fetch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: email.value }),
  })
  const resp = await req.json()
  fetchEmailResp.value = resp

  if (resp?.registry) {
    regFlag.value = true
    lock.value = true
    pinFlag.value = true
    msgFlag.value = true
    step1.value = false
    const masked = maskEmail(email.value)
    msg.value = `Welcome to Rana! <b>Verify your email</b> Please enter the verification code sent to: ${masked}`
  } else if (resp.msg === 'Welcome back, please setup your 2fa.') {
    step1.value = false
    pinFlag.value = true
    token.value = resp.token
    localStorage.setItem('auth_token', resp.token)
    setup2fa()
  } else {
    lock.value = true
    pinFlag.value = true
    msgFlag.value = true
    step1.value = false
    msg.value = `Welcome back <b>${email.value}</b>, <br/> please input the code from your auth app.`
  }
}

function maskEmail(email: string) {
  const [localPart, domain] = email.split('@')
  if (!localPart || !domain) return email
  const visibleStart = localPart.slice(0, 2)
  const visibleEnd = localPart.slice(-2)
  return `${visibleStart}***${visibleEnd}@${domain}`
}

async function login() {
  const pinCode = pin.value.join('')
  const registry = fetchEmailResp.value?.registry
  const req = await fetch(`${api}totp/login`, {
    method: 'POST',
    body: JSON.stringify({ email: email.value, secret: fetchEmailResp.value?.secret, code: pinCode, registry }),
  })
  if (!req.ok && req.status === 401) {
    pin.value = []
    alertFlag.value = true
    msg.value = 'Invalid Code'
    clearMSG()
    return
  }
  const resp = await req.json()

  if (!registry && resp.token) {
    const authToken = useAuthToken()
    const authEmail = useAuthEmail()
    authToken.value = resp.token
    authEmail.value = email.value
    const tokenCookie = useCookie('auth_token', { path: '/' })
    tokenCookie.value = resp.token
    localStorage.setItem('auth_token', resp.token)
  } else {
    token.value = resp.token
    localStorage.setItem('auth_token', resp.token)
    tos.value = true
    setup2fa()
  }
 

  emit('closeLogin')
 
}

async function setup2fa() {
  pin.value = []
  pinFlag.value = true
  valKey.value = 1
  msg.value = `<b>Setup Two Factor Authentication</b> Scan the QR code using your auth app and enter the code.`

  const resp = await fetch(`${api}totp/assign?email=${email.value}`, { method: 'GET', headers: { Authorization: `Bearer ${token.value}` } })
  const qrResp = await resp.json()
  qrImg.value = qrResp.qrUrl
}

async function conf2fa() {
  const pinCode = pin.value.join('')
  const req = await fetch(`${api}totp/verify`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token.value}` },
    body: JSON.stringify({ email: email.value, code: pinCode }),
  })
  await req.json()
  msg.value = `<b>Location + DOB</b> These are only needed for compliance.`
  pinFlag.value = false
  qrImg.value = null
  infoFlag.value = true
}

async function handleTos(data: { dateOfBirth: string; region: string }) {
  userInfo.value = data
  await submitInfo(data)
  await tosSubmission()
}

async function submitInfo(data: any) {
  await fetch(`${api}api-users/profile`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.value}` },
    body: JSON.stringify({ region: data.region, dateOfBirth: data.dateOfBirth }),
  })

  emit('closeLogin');
}

async function tosSubmission() {
  await fetch(`${api}totp/tos`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token.value}` },
    body: JSON.stringify({ email: email.value }),
  })
}

onMounted(() => { resetForm() })
</script>

<style scoped>
/* You can adjust these styles or remove if unneeded */
.drawer-right {
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 400px;
  height: 100vh;
  transform: translateX(100%);
  transition: transform 0.3s ease-out;
}

.drawer-right[data-state="open"] {
  transform: translateX(0);
}

.drawer-right[data-state="closed"] {
  transform: translateX(100%);
}
.slide-in-right {
  animation: slideInRight 0.3s forwards;
}

@keyframes slideInRight {
  from {
    transform: translateX(10px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}
/* Styles for auto-fill customization */
input:-webkit-autofill,
input:-webkit-autofill:hover,
input:-webkit-autofill:focus,
input:-webkit-autofill:active {
  -webkit-box-shadow: 0 0 0 30px #021B27 inset !important;
  -webkit-text-fill-color: #00CAAA !important;
  transition: background-color 5000s ease-in-out 0s;
  border-color: #00CAAA !important;
}

/* Firefox specific */
input:autofill {
  background-color: #021B27 !important;
  color: #00CAAA !important;
  border-color: #00CAAA !important;
}

/* Animation trick to override persistent browser styles */
@keyframes autofillFix {
  to {
    background-color: transparent;
    color: inherit;
  }
}

input:-webkit-autofill {
  animation-name: autofillFix;
  animation-fill-mode: both;
}
</style>
